import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, Output, QueryList, TemplateRef } from '@angular/core';
import { DataTableCellDirective } from './data-table-cell.directive';

export interface DataTableColumn {
  key: string;
  header: string;
  align?: 'left' | 'center' | 'right';
  sticky?: boolean;
}

export interface DataTablePageMeta {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
})
export class DataTableComponent implements AfterContentInit {
  @Input({ required: true }) columns: DataTableColumn[] = [];
  @Input() rows: any[] = [];
  @Input() loading = false;
  @Input() emptyMessage = 'No records found';
  @Input() trackBy: (index: number, row: any) => any = (index) => index;
  @Input() meta: DataTablePageMeta | null = null;
  @Input() pageSize: number | null = null;
  @Input() pageSizeOptions: number[] = [15, 20, 30, 40, 50];

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  @ContentChildren(DataTableCellDirective) cellTemplates: QueryList<DataTableCellDirective>;

  private cellTemplateMap = new Map<string, TemplateRef<any>>();

  ngAfterContentInit(): void {
    this.rebuildCellTemplateMap();
    this.cellTemplates.changes.subscribe(() => this.rebuildCellTemplateMap());
  }

  cellTemplateFor(columnKey: string): TemplateRef<any> | null {
    return this.cellTemplateMap.get(columnKey) ?? null;
  }

  columnValue(row: any, key: string): any {
    return row?.[key];
  }

  get rangeStart(): number {
    if (!this.meta?.total) {
      return 0;
    }

    return (this.meta.current_page - 1) * this.meta.per_page + 1;
  }

  get rangeEnd(): number {
    if (!this.meta?.total) {
      return 0;
    }

    return Math.min(this.meta.current_page * this.meta.per_page, this.meta.total);
  }

  get pageNumbers(): Array<number | '...'> {
    const lastPage = this.meta?.last_page ?? 1;
    const currentPage = this.meta?.current_page ?? 1;

    if (lastPage <= 5) {
      return Array.from({ length: lastPage }, (_, index) => index + 1);
    }

    const seed = new Set<number>([1, 2, 3, currentPage - 1, currentPage, currentPage + 1, lastPage]);
    const pages = Array.from(seed)
      .filter((page) => page >= 1 && page <= lastPage)
      .sort((a, b) => a - b);

    const result: Array<number | '...'> = [];

    pages.forEach((page, index) => {
      if (index > 0 && page - pages[index - 1] > 1) {
        result.push('...');
      }

      result.push(page);
    });

    return result;
  }

  goToPage(page: number | '...'): void {
    if (typeof page !== 'number' || !this.meta) {
      return;
    }

    if (page < 1 || page > this.meta.last_page || page === this.meta.current_page) {
      return;
    }

    this.pageChange.emit(page);
  }

  onPageSizeChange(event: Event): void {
    const size = Number((event.target as HTMLSelectElement).value);

    if (size) {
      this.pageSizeChange.emit(size);
    }
  }

  private rebuildCellTemplateMap(): void {
    this.cellTemplateMap.clear();
    this.cellTemplates.forEach((cell) => this.cellTemplateMap.set(cell.columnKey, cell.template));
  }
}
